import { FC, useEffect, useState } from 'react';
import sortBy from 'lodash/sortBy';
import classnames from 'classnames';
import { useRecoilValue } from 'recoil';
import { Props, PageData } from './types';

import { translationDataState } from '../../../../store';
import Icon from '@/units/Icon';
import { Loader } from '@/units/Iframe/style';
import { Root, Header, Content, List, Main, Footer } from '@/components/tour/game/gameBook/styled';
import { BannerHorizontalViewOnly } from '../BannerHorizontalViewOnly/BannerHorizontalViewOnly';

const GameBook: FC<Props> = ({ onClose, book, unlockedChapterIds, tryButtonPlaySound, show, moveToSweep }) => {
  const { backgroundColor, backgroundOpacity, textColor, buttonColor, chapterLocked } = book;

  if (!show) return null;

  const [activePage, setActivePage] = useState<PageData>();
  const [pages, setPages] = useState<PageData[]>([]);
  const translation = useRecoilValue(translationDataState);

  useEffect(() => {
    if (book?.chapters?.length > 0) {
      const data: PageData[] = sortBy(book.chapters, 'order').reduce(
        (acc, chapter) => {
          acc.push({
            id: chapter.id,
            title: chapter.title,
            description: chapter.description,
            url: chapter.img ? `${process.env.REACT_APP_AWS}/games/${book.gameId}/book/${book.id}/chapter/${chapter.id}/${chapter.img}` : undefined,
            moveToSweepUuid: chapter.sweepUuid,
          });
          return acc;
        },
        [
          {
            id: 0,
            title: translation?.experienceCover || 'Cover',
            description: book.description,
            url: book.img ? `${process.env.REACT_APP_AWS}/games/${book.gameId}/book/${book.id}/${book.img}` : undefined,
            moveToSweepUuid: undefined,
          },
        ],
      );
      setPages(data);
      setActivePage(data[0]);
    }
  }, [book]);

  useEffect(() => {
    if (activePage && !activePage.url) {
      if (activePage.moveToSweepUuid) {
        moveToSweep(activePage.moveToSweepUuid);
      } else if (activePage.id !== 0) {
        onClose();
      }
    }
  }, [activePage]);

  const isPageLocked = (chapterId: number) => {
    if (chapterId === 0 || !chapterLocked) return false;
    return !unlockedChapterIds.includes(chapterId);
  };

  const changePage = (page: PageData) => {
    if (!isPageLocked(page.id)) {
      tryButtonPlaySound();
      setActivePage(page);
    }
  };

  return (
    <>
      <BannerHorizontalViewOnly onClose={onClose} show={show} />
      <Root background={backgroundColor} opacity={backgroundOpacity} color={textColor}>
        <Header>
          <h2>{book?.description || translation?.expJournBook || 'Journey book'}</h2>
          <Icon.Close className="close-modal" onClick={onClose} fontSize="large" />
        </Header>
        <Content>
          <Main>
            {activePage ? activePage.url ? <img className="book" src={activePage.url} alt="" /> : <p className="page-description">{activePage.description}</p> : <Loader />}
            <Footer show={!!activePage?.moveToSweepUuid} buttonColor={buttonColor} color={textColor}>
              <button onClick={() => moveToSweep(activePage.moveToSweepUuid)}>
                {translation?.experienceTeleport || 'Take me there'} <Icon.ArrowRight fontSize="inherit" />
              </button>
            </Footer>
          </Main>
          <List color={textColor} buttonColor={buttonColor}>
            {(pages || []).map((page) => (
              <li
                className={classnames('chapter', isPageLocked(page.id) && 'locked', !isPageLocked(page.id) && activePage.id === page.id && 'active')}
                key={page.id}
                onClick={() => changePage(page)}
              >
                {page.title}
                {isPageLocked(page.id) && <Icon.Lock fontSize="inherit" />}
              </li>
            ))}
          </List>
        </Content>
      </Root>
    </>
  );
};
export default GameBook;
